import { MenuService } from '@captiveaire/menu';
import { Observable, Subscription } from 'rxjs';
import { IDeviceStatusListSummary } from '../shared/models/device-status-list-summary.interface';
import { DeviceStatusService } from '../shared/services/device-status.service';
import { deviceStatus, EventTypes as CommonEventTypes, MessageType } from '@caslink/common-client';
import { AppContextService } from '@caslink/common-client/ng-app/appContext/AppContextService';
import { CommonBroadcastService } from '@caslink/common-client/ng-app/common-broadcast/common-broadcast.service';
import { ModuleViewNavigationService } from '@caslink/common-client/ng-app/moduleViewNavigationService/ModuleViewNavigationService';
import { DialogService } from '@caslink/common-client/ng-app/dialog-service/dialog.service';
import { DeviceStatusViewInfoQueryService } from '../shared/services/device-status-view-info-query.service';
import { DeviceStatusFiltersHandler } from '../shared/services/device-status-filter-handler.service';
import { DetailViewService } from '../shared/services/detail-view.service';
import { DeviceMenuItemsService } from '../shared/services/device-menu-items.service';
import { DeviceStatusLoadedLocationBaseComponent } from '../shared/components/device-status-loaded-location-base.component';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { EventTypes } from '../shared/models/event-types.enum';
import { StateService } from '@uirouter/core';

@Component({
    selector: 'dl-device-detail-view',
    templateUrl: './device-detail-view.component.html',
    styleUrls: ['./device-detail-view.component.scss']
})
export class DeviceDetailViewComponent extends DeviceStatusLoadedLocationBaseComponent implements OnInit, OnDestroy {
    static $name: string = 'dlDeviceDetailView';

    loading: boolean;
    lockOpened = true;

    @Input() hideFields: boolean;
    @Input() locationId: number;
    @Input() jobNumber: number;
    @Input() moduleId: number;
    /**
     * Used in Wizard to hide filters, if this is set, do not use query cache service at all
     */
    @Input() hideFilters: boolean;
    @Input() reinitialize: Observable<void> = null;

    private toggleReadersState: boolean = true;
    private allDevices: IDeviceStatusListSummary[];
    private deviceStatusUpdateSubscription: Subscription;
    private reinitializeSubscription: Subscription;

    constructor(
        public deviceStatusService: DeviceStatusService,
        public dialogService: DialogService,
        public deviceStatusFilters: DeviceStatusFiltersHandler,
        public detailViewService: DetailViewService,
        public appContextService: AppContextService,
        private stateService: StateService,
        private menuService: MenuService,
        private moduleViewNavigationService: ModuleViewNavigationService,
        private commonBroadcastService: CommonBroadcastService,
        private deviceStatusViewInfoQueryService: DeviceStatusViewInfoQueryService,
        private deviceMenuItemsFactory: DeviceMenuItemsService
    ) {
        super();
    }

    ngOnInit() {
        this.initReinitializeSubscription();
        this.setBindings();
        this.initDetailService();
        this.initDropdowns();
        this.getDetail();
        this.filterMenu();
        this.subscribeToEvents();
    }

    ngOnDestroy() {
        const query = this.detailViewService.query;

        query.wildcard = this.detailViewService.filter.wildcard;
        query.hardwareCategory = this.detailViewService.filter.category;
        query.hardwareCategoryId = this.detailViewService.filter.categoryId;

        if (!this.hideFields) {
            this.deviceStatusViewInfoQueryService.setQuery(query);
        }

        this.stopUpdates();
        this.deviceStatusUpdateSubscription?.unsubscribe();
        this.reinitializeSubscription?.unsubscribe();
    }

    viewDetail(item: IDeviceStatusListSummary) {
        this.moduleViewNavigationService.viewHardwareItem(
            item.hardwareItem.id,
            item.locationId,
            item.hardwareItem.moduleId,
            item.macAddress,
            this.detailViewService.query);
    }

    viewJson(item: IDeviceStatusListSummary) {
        this.stateService.go(deviceStatus.deviceJsonViewer, {
            category: item.category,
            hardwareItemId: item.hardwareItem.id,
            query: this.detailViewService.query
        });
    }

    deleteDevice(item: IDeviceStatusListSummary) {
        const options = {
            data: {
                confirmBtnText: 'Yes',
                message: 'Would you really like to remove this Device?',
                messageType: MessageType.Warning,
                showCancelBtn: true,
                title: 'Remove?'
            }
        };

        const dialogRef = this.dialogService.showConfirmDialog(options);

        dialogRef
            .afterClosed()
            .toPromise()
            .then(isConfirmed => {
                if (!isConfirmed) return;

                this.deviceStatusService.delete(item)
                    .finally(() => this.getDetail());
            });
    }

    getDetail() {
        if (this.isQueryValid()) {
            this.loading = true;
            return this.deviceStatusService.getDetailLocationView(this.detailViewService.query)
                .then(response => {
                    this.detailViewService.data = response;
                    this.toggleReadersState = true;
                    this.stopUpdates();
                    this.startUpdates();
                })
                .finally(() => { this.loading = false; }) as Promise<any>;
        } else {
            this.detailViewService.data = null;
        }
    }

    private stopUpdates() {
        this.deviceStatusService.stopStatusUpdates(this.loadedLocationId, this.allDevices, this.detailViewService.query.locationId);
    }

    private startUpdates() {
        if (!this.detailViewService.data.modules) {
            return;
        }
        let devices = [];
        this.detailViewService.data.modules.forEach(m => {
            devices = devices.concat(m.devices);
        });

        this.allDevices = devices;
        this.deviceStatusService.startStatusUpdates(this.loadedLocationId, this.allDevices, this.detailViewService.query.locationId);
    }

    private initDetailService() {
        const query = this.deviceStatusViewInfoQueryService.getQuery();

        this.detailViewService.init(this.locationId, this.moduleId, this.jobNumber, query);

        // if hide fields is used, filter parameters should be OR and we do not use caching
        if (this.hideFields) {
            this.detailViewService.query.moduleOrLocationOrJobNumber = true;
        }

        this.detailViewService.getData = () => this.getDetail();
    }

    private initDropdowns() {
        this.deviceStatusFilters.init(this.detailViewService.query, () => this.getDetail());

        this.deviceStatusFilters.initDropdowns(false, this.hideFields);
    }

    private isQueryValid(): boolean {
        const query = this.detailViewService.query;

        return query.jobNumber > 0 || query.locationId > 0 || query.moduleId > 0;
    }

    toggleReaderView() {
        this.commonBroadcastService.broadcast(EventTypes.ToggleReaderView, { show: this.toggleReadersState });
        this.toggleReadersState = !this.toggleReadersState;
    }

    private filterMenu() {
        this.menuService.filterMenu(this.deviceMenuItemsFactory.getMenuItems(this))
            .then(response => {
                this.detailViewService.menuItems = response;
            });
    }

    private setBindings() {
        // get rid of zeros
        this.locationId = this.locationId ? this.locationId : null;
        this.moduleId = this.moduleId ? this.moduleId : null;
        this.jobNumber = this.jobNumber ? this.jobNumber : null;
    }

    private subscribeToEvents() {
        this.deviceStatusUpdateSubscription = this.commonBroadcastService.on(CommonEventTypes.DeviceStatusUpdate)
            .subscribe((data: IDeviceStatusListSummary) => {
                this.deviceStatusService.deviceStatusUpdate(this.allDevices, data);
            });
    }

    private initReinitializeSubscription() {
        this.reinitializeSubscription = this.reinitialize?.subscribe(() => {
            this.initDetailService();
            this.getDetail();
        });
    }
}
